import React from "react";
import { useTranslation } from "react-i18next";
import { HiCheck, HiExclamationCircle } from "react-icons/hi";
import { FaCopy } from "react-icons/fa";
import { twMerge } from "tailwind-merge";
import { CustomButton, CustomButtonSize } from "./CustomButton";
import { useCopyToClipboard } from "./useCopyToClipboard";

interface CopyToClipboardButtonProps {
  textToCopy?: string;
  onBeforeCopy?: () => Promise<string | null | undefined>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: "primary" | "secondary" | "outline" | "none";
  size?: CustomButtonSize;
  fullWidth?: boolean;
  className?: string;
  label?: React.ReactNode;
  /** A client page's own texts: the dashboard's exist in French and English only. */
  translationNamespace?: string;
}

export const CopyToClipboardButton = ({
  textToCopy,
  onBeforeCopy,
  onSuccess,
  onError,
  disabled = false,
  isLoading = false,
  variant = "primary",
  size,
  fullWidth = true,
  className: classNameProp,
  label,
  translationNamespace,
}: CopyToClipboardButtonProps) => {
  const { t } = useTranslation(translationNamespace);
  const { status, copy } = useCopyToClipboard({ onSuccess, onError });

  const isProcessing = isLoading || status === "copying";

  const handleCopy = () => {
    if (disabled || isProcessing) return;
    if (!textToCopy && !onBeforeCopy) return;
    void copy(onBeforeCopy ?? (textToCopy as string));
  };

  const isButtonDisabled =
    disabled || isProcessing || (!textToCopy && !onBeforeCopy);

  const isIdle = status === "idle" && !isProcessing;
  const resolvedLabel =
    status === "success"
      ? (t("common.copied") as string)
      : status === "error"
      ? (t("common.copyFailed") as string)
      : isProcessing
      ? (t("common.copying") as string)
      : isIdle && label
      ? label
      : (t("common.copyLink") as string);

  const showSpinner = isProcessing;

  const spinnerColor =
    variant === "primary"
      ? "border-white/80 border-t-transparent"
      : "border-blue-600/80 border-t-transparent";

  const icon = showSpinner ? (
    <span
      className={`h-4 w-4 animate-spin rounded-full border-2 ${spinnerColor}`}
    />
  ) : status === "success" ? (
    <HiCheck size={16} />
  ) : status === "error" ? (
    <HiExclamationCircle size={16} />
  ) : (
    <FaCopy size={16} />
  );

  const statusClassName =
    status === "success"
      ? "bg-green-600 enabled:hover:bg-green-700 text-white"
      : status === "error"
      ? "bg-red-600 enabled:hover:bg-red-700 text-white"
      : "";

  return (
    <CustomButton
      variant={variant}
      size={size}
      onClick={handleCopy}
      disabled={isButtonDisabled}
      className={twMerge(
        fullWidth ? "w-full sm:w-auto" : "w-auto",
        size ? "" : "px-4 sm:px-6",
        "transition-colors duration-200",
        classNameProp,
        statusClassName
      )}
      titleClassName="transition-none"
      title={
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
          {icon}
          <span>{resolvedLabel}</span>
        </div>
      }
    />
  );
};

export default CopyToClipboardButton;
